import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Button, Card, FormField, Spinner } from "../../../components/ui";
import { ApiError } from "../../../lib/api/client";
import { AppShell } from "../../../layouts/AppShell";
import {
  getQuestionnaire,
  listQuestionnaireVersions,
  updateAvailability,
} from "../api/questionnaireAdminApi";
import { AvailabilityDialog } from "../components/AvailabilityDialog";

function toDatetimeLocal(value?: string | null) {
  return value ? new Date(value).toISOString().slice(0, 16) : "";
}

export function QuestionnaireManagePage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ["questionnaire"], queryFn: getQuestionnaire });
  const versionsQuery = useQuery({
    queryKey: ["questionnaire", "versions"],
    queryFn: listQuestionnaireVersions,
  });
  const [availabilityEdits, setAvailabilityEdits] = useState<{
    opensAt?: string;
    closesAt?: string;
  }>({});
  const [next, setNext] = useState<"OPEN" | "CLOSED">();
  const opensAt =
    availabilityEdits.opensAt ?? toDatetimeLocal(query.data?.availability.opensAt);
  const closesAt =
    availabilityEdits.closesAt ?? toDatetimeLocal(query.data?.availability.closesAt);

  const availabilityMutation = useMutation({
    mutationFn: async (status: "OPEN" | "CLOSED") =>
      updateAvailability(
        {
          status,
          opensAt: opensAt ? new Date(opensAt).toISOString() : null,
          closesAt: closesAt ? new Date(closesAt).toISOString() : null,
        },
        query.data!.rowVersion,
      ),
    onSuccess: (data) => {
      client.setQueryData(["questionnaire"], data);
      setNext(undefined);
    },
    onError: (error) => {
      setNext(undefined);
      if (error instanceof ApiError && error.problem.status === 412) {
        void client.invalidateQueries({ queryKey: ["questionnaire"] });
      }
    },
  });

  return (
    <AppShell title="問卷管理" breadcrumb={["管理後台", "固定問卷"]}>
      {(query.isLoading || versionsQuery.isLoading) && <Spinner label="載入問卷" />}
      {query.data && (
        <Card title={query.data.title}>
          <Alert variant="info">
            目前發布版本：V{query.data.versionNumber}。草稿編輯已移到獨立頁面；發布新版本後，舊填答會保留封存，但不再列入現行版本統計與名冊。
          </Alert>
          <p>
            目前狀態：
            <strong>
              {query.data.availability.status === "OPEN" ? "開放中" : "已截止"}
            </strong>
          </p>
          <FormField
            label="開放時間（選填）"
            type="datetime-local"
            value={opensAt}
            onChange={(event) =>
              setAvailabilityEdits((current) => ({
                ...current,
                opensAt: event.target.value,
              }))
            }
          />
          <FormField
            label="截止時間（選填）"
            type="datetime-local"
            value={closesAt}
            onChange={(event) =>
              setAvailabilityEdits((current) => ({
                ...current,
                closesAt: event.target.value,
              }))
            }
          />
          <Button
            loading={availabilityMutation.isPending}
            onClick={() =>
              setNext(query.data.availability.status === "OPEN" ? "CLOSED" : "OPEN")
            }
          >
            {query.data.availability.status === "OPEN" ? "截止收件" : "開放收件"}
          </Button>{" "}
          <a className="btn btn-primary" href="/admin/questionnaire/draft">
            編輯問卷草稿
          </a>{" "}
          <a className="btn btn-secondary" href="/admin/questionnaire/preview">
            預覽目前版本
          </a>
          <a className="btn btn-info ml-2" href="/admin/questionnaire/results">
            查看填答結果與名冊
          </a>
          {availabilityMutation.isError && (
            <Alert variant="danger">狀態更新失敗，請重新載入後再試。</Alert>
          )}
        </Card>
      )}
      <Card title="版本紀錄">
        <ul className="mb-0">
          {(versionsQuery.data ?? []).map((version) => (
            <li key={version.id}>
              V{version.versionNumber} - {version.title}（
              {new Date(version.publishedAt).toLocaleString()}）
            </li>
          ))}
        </ul>
      </Card>
      <AvailabilityDialog
        open={Boolean(next)}
        nextStatus={next ?? "CLOSED"}
        onClose={() => setNext(undefined)}
        onConfirm={() => next && availabilityMutation.mutate(next)}
      />
    </AppShell>
  );
}
